/**
 * Saisie décimale tolérante virgule/point.
 *
 * Convention tunisienne : la virgule est le séparateur décimal affiché
 * (`1 234,567 DT`), mais les claviers (surtout mobiles) proposent tantôt
 * `,` tantôt `.`. Ces helpers acceptent les deux à la saisie et
 * normalisent : point pour la machine (`Number()`), virgule pour
 * l'affichage. Les chiffres arabes (٠-٩ / ۰-۹) et le séparateur décimal
 * arabe (٫) sont également acceptés.
 */

const ARABIC_INDIC_ZERO = 0x0660; // ٠..٩
const EXTENDED_ARABIC_INDIC_ZERO = 0x06f0; // ۰..۹
const ARABIC_DECIMAL_SEPARATOR = '٫'; // ٫

/** Chiffres arabes → latins, ٫ → virgule. Le reste est inchangé. */
function latinize(raw: string): string {
  let out = '';
  for (const ch of raw) {
    const code = ch.codePointAt(0) ?? 0;
    if (code >= ARABIC_INDIC_ZERO && code <= ARABIC_INDIC_ZERO + 9) {
      out += String(code - ARABIC_INDIC_ZERO);
    } else if (code >= EXTENDED_ARABIC_INDIC_ZERO && code <= EXTENDED_ARABIC_INDIC_ZERO + 9) {
      out += String(code - EXTENDED_ARABIC_INDIC_ZERO);
    } else if (ch === ARABIC_DECIMAL_SEPARATOR) {
      out += ',';
    } else {
      out += ch;
    }
  }
  return out;
}

/**
 * Texte libre (saisi ou collé) → chaîne machine avec `.` décimal, ou `''`.
 *
 * Gère les deux séparateurs, les espaces/espaces insécables de groupement
 * et les collages type `1.234,56` / `1,234.56` (le séparateur qui apparaît
 * en DERNIER est le décimal, l'autre est un séparateur de milliers).
 * Un séparateur unique est toujours décimal ; répété (`1.234.567`), c'est
 * un séparateur de milliers.
 */
export function normalizeDecimalText(raw: string): string {
  let s = latinize(raw);
  // Ne garder que chiffres, séparateurs et signe.
  s = s.replace(/[^0-9.,-]/g, '');
  // Signe : uniquement un `-` de tête.
  const negative = s.startsWith('-');
  s = s.replace(/-/g, '');

  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  if (lastComma !== -1 && lastDot !== -1) {
    // Les deux présents : le dernier est le séparateur décimal.
    const decimalSep = lastComma > lastDot ? ',' : '.';
    const groupSep = decimalSep === ',' ? '.' : ',';
    s = s.split(groupSep).join('');
    const idx = s.lastIndexOf(decimalSep);
    s = s.slice(0, idx).split(decimalSep).join('') + '.' + s.slice(idx + 1);
  } else if (lastComma !== -1 || lastDot !== -1) {
    const sep = lastComma !== -1 ? ',' : '.';
    const first = s.indexOf(sep);
    const last = s.lastIndexOf(sep);
    if (first === last) {
      // Unique → décimal.
      s = s.replace(sep, '.');
    } else {
      // Répété → milliers.
      s = s.split(sep).join('');
    }
  }

  if (s === '' || s === '.') return '';
  return (negative ? '-' : '') + s;
}

/**
 * Parse tolérant : accepte nombre, `1,5`, `1.5`, `1 234,56`, `٣٫٥`…
 * Retourne `NaN` si vide ou illisible (même contrat que
 * `register(..., { valueAsNumber: true })` sur un champ vide).
 */
export function parseDecimal(raw: string | number | null | undefined): number {
  if (typeof raw === 'number') return raw;
  if (raw == null) return NaN;
  const normalized = normalizeDecimalText(raw);
  if (normalized === '' || normalized === '-') return NaN;
  return Number(normalized);
}

export interface DecimalTypingOptions {
  /** Nombre max de décimales (0 = entier, le séparateur est refusé). */
  maxDecimals: number;
  /** Autoriser un `-` de tête. */
  allowNegative: boolean;
}

/**
 * Filtre de frappe : garde chiffres + UN séparateur (`,` ou `.`, tel que
 * tapé — l'autocorrection en virgule se fait au blur), tronque au-delà de
 * `maxDecimals`, ignore tout le reste. Ne reformate jamais ce que
 * l'utilisateur est en train de taper (les zéros de fin restent affichés).
 */
export function sanitizeDecimalTyping(raw: string, opts: DecimalTypingOptions): string {
  const { maxDecimals, allowNegative } = opts;
  let out = '';
  let sepSeen = false;
  let decimals = 0;
  for (const ch of latinize(raw)) {
    if (ch >= '0' && ch <= '9') {
      if (sepSeen) {
        if (decimals >= maxDecimals) continue;
        decimals++;
      }
      out += ch;
    } else if ((ch === ',' || ch === '.') && !sepSeen && maxDecimals > 0) {
      sepSeen = true;
      out += ch;
    } else if (ch === '-' && allowNegative && out === '') {
      out += ch;
    }
  }
  return out;
}

/**
 * Valeur → texte d'affichage au repos : virgule décimale, zéros de fin
 * retirés, `''` pour vide/NaN. Pas de groupement de milliers (champ
 * éditable).
 *
 * `maxDecimals` n'arrondit que lorsque c'est sans perte (bruit flottant
 * type 0.30000000000000004) : une valeur réellement plus précise que
 * `maxDecimals` (donnée legacy) est affichée fidèlement — le champ ne doit
 * jamais afficher un nombre différent de la valeur enregistrée.
 */
export function formatDecimalValue(
  value: number | string | null | undefined,
  maxDecimals: number,
): string {
  const n = typeof value === 'string' ? parseDecimal(value) : value;
  if (n == null || typeof n !== 'number' || !Number.isFinite(n)) return '';
  let s = n.toFixed(Math.max(0, Math.min(20, maxDecimals)));
  if (Math.abs(Number(s) - n) > 1e-9) {
    // L'arrondi changerait la valeur → représentation exacte.
    s = String(n);
    if (s.includes('e') || s.includes('E')) s = n.toFixed(12);
  }
  if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '');
  // -0 → 0
  if (s === '-0') s = '0';
  return s.replace('.', ',');
}
